import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const ThemeCtx = createContext(null);
const AuthCtx = createContext(null);
const CartCtx = createContext(null);

export const useTheme = () => useContext(ThemeCtx);
export const useAuth = () => useContext(AuthCtx);
export const useCart = () => useContext(CartCtx);

function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem("wb_theme") || "dark");
  useEffect(() => {
    const el = document.documentElement;
    el.classList.remove("dark", "light");
    el.classList.add(theme);
    localStorage.setItem("wb_theme", theme);
  }, [theme]);
  const toggle = () => setTheme((t) => (t === "dark" ? "light" : "dark"));
  return <ThemeCtx.Provider value={{ theme, toggle }}>{children}</ThemeCtx.Provider>;
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const refresh = useCallback(async () => {
    localStorage.removeItem("wb_token");
    try {
      const { data } = await api.get("/auth/session");
      setUser(data.user || false);
    } catch {
      setUser(false);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const signIn = ({ user: u }) => setUser(u);
  const logout = () => {
    setUser(false);
    api.post("/auth/logout").catch((e) => console.warn("Logout request failed", e));
  };
  const can = (p) => !!user?.permissions?.includes(p);
  return <AuthCtx.Provider value={{ user, signIn, logout, can }}>{children}</AuthCtx.Provider>;
}

function CartProvider({ children }) {
  const [items, setItems] = useState(() => JSON.parse(localStorage.getItem("wb_cart") || "[]"));
  useEffect(() => { localStorage.setItem("wb_cart", JSON.stringify(items)); }, [items]);

  const add = (p, qty = 1) => {
    if (p.sold_out) return toast.error("This artwork is sold out");
    const existing = items.find((i) => i.product_id === p.id);
    if (existing && p.is_digital) return toast.message(`${p.title} is already in your cart`);
    const max = p.is_digital ? 1 : p.stock;
    const next = Math.min((existing?.quantity || 0) + qty, max);
    if (existing && next === existing.quantity) return toast.error(`Only ${max} available`);
    const entry = {
      product_id: p.id, slug: p.slug, title: p.title, type: p.type, price: p.price,
      image_url: p.image_urls?.[0], quantity: next, max, is_digital: p.is_digital,
    };
    setItems((prev) => (existing ? prev.map((i) => (i.product_id === p.id ? entry : i)) : [...prev, entry]));
    toast.success(`${p.title} added to cart`);
  };
  const setQty = (id, q) =>
    setItems((prev) => prev.map((i) => (i.product_id === id ? { ...i, quantity: Math.max(1, Math.min(q, i.max)) } : i)));
  const remove = (id) => setItems((prev) => prev.filter((i) => i.product_id !== id));
  const clear = useCallback(() => setItems([]), []);
  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const hasPhysical = items.some((i) => i.type === "physical_art");
  return (
    <CartCtx.Provider value={{ items, add, setQty, remove, clear, count, subtotal, hasPhysical }}>
      {children}
    </CartCtx.Provider>
  );
}

export function AppProviders({ children }) {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CartProvider>{children}</CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
